export function formatProductSize(p: any): string {
  try {
    const runner = String(p?.specifications?.runner || '').trim()
    const bath = String(p?.specifications?.bathMat || '').trim()
    const dims = String(p?.specifications?.dimensions || '').trim()

    function extractCompact(s: string): string | '' {
      if (!s) return ''
      const inMatch = /(\d{1,3})\s*[×x]\s*(\d{1,3})\s*(?:in|inch|inches)\b/i.exec(s)
      if (inMatch) return `${inMatch[1]}x${inMatch[2]} in`
      const cmMatch = /(\d{1,3})\s*[×x]\s*(\d{1,3})\s*cm\b/i.exec(s)
      if (cmMatch) return `${cmMatch[1]}x${cmMatch[2]} cm`
      return ''
    }

    const parts: string[] = []
    const r = extractCompact(runner)
    const b = extractCompact(bath)
    if (r) parts.push(r)
    if (b) parts.push(b)
    if (!parts.length) {
      const d = extractCompact(dims)
      if (d) parts.push(d)
    }
    return parts.join(' + ')
  } catch {
    return ''
  }
}

export function buildProductMetaDescription(p: any): string {
  if (!p) return ''
  const title = p.title || ''
  const category = p.category || ''
  const sizeText = formatProductSize(p)
  const material = p?.specifications?.material || p.material || ''
  const colourList = (p?.variants?.options || []).map((o: any) => o.label).filter(Boolean)
  const colours = colourList.length ? `Colors: ${colourList.slice(0, 6).join(', ')}.` : ''
  const points = (p.bulletPoints || []).slice(0, 2).map((s: any) => String(s).replace(/^[^A-Za-z0-9]+/g, ''))
  const features = points.length ? points.join(' ') : ''
  // Compose SEO keywords: merge product keywords with ensured terms based on category/backing
  const rawKeywords = Array.from(new Set([...(p.keywords || [])].map((k: any) => String(k).toLowerCase()).filter(Boolean)))
  const ensure: string[] = []
  const cat = String(category || '').toLowerCase()
  if (/runner/.test(cat)) ensure.push('runner rug', 'bedside runner', 'runner rugs')
  if (/floor|mat/.test(cat)) ensure.push('floor mats', 'door mats')
  const backing = deriveBacking(p)
  if (backing && /anti/i.test(backing)) ensure.push('soft anti skid')
  const merged = Array.from(new Set([...rawKeywords, ...ensure])).slice(0, 6)
  const keywordsLine = merged.length ? `Keywords: ${merged.join(', ')}.` : ''
  const parts = [
    `${title} — ${category}.`,
    sizeText ? `Size: ${sizeText}.` : '',
    material ? `Material: ${material}.` : '',
    colours,
    keywordsLine,
    features,
  ].filter(Boolean)
  const text = parts.join(' ').replace(/\s+/g, ' ').trim()
  return text.length > 165 ? `${text.slice(0, 162)}...` : text
}

export function deriveBacking(p: any): string | null {
  try {
    const fromSpec = p?.specifications?.backing
    if (fromSpec) return fromSpec
    const material = String(p?.specifications?.material || p?.material || '').toLowerCase()
    const points = (p?.bulletPoints || []).join(' ').toLowerCase()
    const hasAntiSkid = /(anti\s*-?skid|non\s*-?slip)/i.test(material) || /(anti\s*-?skid|non\s*-?slip)/i.test(points)
    const hasTPR = /tpr/i.test(material) || /tpr/i.test(points)
    if (hasTPR && hasAntiSkid) return 'TPR anti‑skid base'
    if (hasAntiSkid) return 'Anti‑skid backing'
    return null
  } catch (_) {
    return null
  }
}

export function titleWithVariant(p: any, label?: string | null): string {
  try {
    const l = (label || '').trim()
    const rawSize = (p?.specifications?.dimensions || p?.specifications?.runner || p?.specifications?.bathMat || '').trim()
    let base: string = String(p?.title || '')

    // Helper: detect any size already present in the title
    const SIZE_RE = /\b\d{1,3}\s*[×x]\s*\d{1,3}(?:\s*(?:in|inch|inches|cm))?\b/i
    const hasSizeAlready = SIZE_RE.test(base)

    // Helper: normalize a spec size to compact "WxH in" form like "24x55 in"
    function normalizeSize(s: string): string | null {
      if (!s) return null
      const mIn = /(\d{1,3})\s*[×x]\s*(\d{1,3})\s*(?:in|inch|inches)\b/i.exec(s)
      if (mIn) return `${mIn[1]}x${mIn[2]} in`
      const mCm = /(\d{1,3})\s*[×x]\s*(\d{1,3})\s*cm\b/i.exec(s)
      if (mCm) return `${mCm[1]}x${mCm[2]} cm`
      return null
    }

    // Insert size only if specs provide one and the title doesn't already include any size
    if (rawSize && !hasSizeAlready) {
      const sizeCompact = normalizeSize(rawSize) || rawSize
      const low = base.toLowerCase()
      const needle = ' by saffron leo'
      const idx = low.indexOf(needle)
      if (idx !== -1) {
        const insertPos = idx + needle.length
        base = base.slice(0, insertPos) + ` (${sizeCompact})` + base.slice(insertPos)
      } else {
        const dashIdx = base.indexOf(' – ')
        base = dashIdx !== -1 ? base.slice(0, dashIdx) + ` (${sizeCompact})` + base.slice(dashIdx) : `${base} (${sizeCompact})`
      }
    }
    return l ? `${base} — ${l}` : base
  } catch {
    return p?.title || ''
  }
}

export function normalizeMetaDescription(input: string, valueProp: string, brand = 'Saffron Leo', min = 150, max = 220): string {
  const clean = String(input || '').replace(/\s+/g, ' ').trim()
  const withBrand = clean.includes(brand) ? clean : (clean ? `${clean} — ${brand}.` : `${brand}.`)
  const withValue = withBrand.length < min ? `${withBrand} ${valueProp}`.replace(/\s+/g, ' ').trim() : withBrand
  if (withValue.length <= max) return withValue
  return `${withValue.slice(0, Math.max(0, max - 3)).trim()}...`
}
